import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import * as Sentry from '@sentry/browser';

import { AuthorizationResponse } from '@/types/openid';
import { persistor, RootState, useAppDispatch } from '@/store';
import { pages } from '@/settings/pages';
import { fetchTokenResponse } from '@/components/pages/Auth/Openid/shared/fetchTokenResponse';
import { OpenIdError } from '@/components/pages/ErrorPage/OpenIdError';
import {
  verifyIdToken,
  decodeIdToken,
} from '@/components/pages/Auth/Openid/shared/verifyIdToken';
import { authStore } from '@/store/auth';
import { fromPathStore } from '@/store/fromPath';
import { affiliationsApi, enabledFeatureFlagsApi } from '@/services/api';
import i18n from '@/settings/i18n';
import { tokenProcessing } from '@/services/api/base/utils';
import { CenteredLoader } from '@/components/utils/Loader';

import { reLogin } from '../../shared/utils';

const selector = ({ openIdVerification, auth, fromPath }: RootState) => ({
  state: openIdVerification.state,
  nonce: openIdVerification.nonce,
  codeVerifier: openIdVerification.codeVerifier,
  token: auth.session?.token,
  fromPath: fromPath.fromPath,
});

export const Container: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [openIdError, setOpenIdError] = useState(false);
  const [returnTo, setReturnTo] = useState<string>();
  const [gloplaId, setGloplaId] = useState<string>();
  const { code, state } = parse(location.search, {
    parseNumbers: true,
  }) as AuthorizationResponse;

  const endInFailure = () => {
    setOpenIdError(true);
    setLoading(false);
  };

  const {
    state: storedState,
    nonce: storedNonce,
    codeVerifier: storedCodeVerifier,
    token: storedToken,
    fromPath: storedFromPath,
  } = useSelector(selector);

  const retrieveReturnTo = (encodedNonce: string): string | undefined => {
    try {
      const nonceJson = JSON.parse(atob(encodedNonce));
      const isObject = typeof nonceJson === 'object';
      const isExpired = new Date(nonceJson.expiresAt) <= new Date();

      if (isObject && !isExpired) {
        if (storedFromPath) {
          return storedFromPath as string;
        } else {
          const { pathname, search } = new URL(nonceJson.returnTo);
          return pathname + search;
        }
      } else {
        throw new Error();
      }
    } catch {
      return undefined;
    }
  };

  const handleErrors = useCallback(
    (error: any) => {
      Sentry.captureException(error, {
        extra: {
          code,
          state,
          storedState,
          storedNonce,
          now: new Date(),
        },
      });
    },
    [code, state, storedNonce, storedState],
  );

  useEffect(() => {
    /**
     * MEMO: 選択した組織情報を取得(affiliationsApi)する前に、現在Reduxに格納されているsession、state、nonce及びcodeVerifierを一旦、リセットする
     * 理由:  Auth側にて現在のログイン状態を確認する際、既にrevokeされたアクセストークンを取得する場合が存在する(ログイン後の所属組織の選択)ため、
     *       401の認証エラーが発生する
     */
    persistor.purge();
    const validForState = state && storedState === state;

    if (code && validForState && storedNonce && storedCodeVerifier) {
      (async () => {
        const tokenResponse = await fetchTokenResponse(
          code,
          storedCodeVerifier,
        );
        const idToken = tokenResponse?.idToken;
        const areOrganizationsSelectTransferred =
          await enabledFeatureFlagsApi.getDetail(
            'organization_select_transfer',
          );

        if (!areOrganizationsSelectTransferred.enabled) {
          // MEMO: decodeで参照するglopla idはsnake_caseなので注意、camelCaseではない
          if (decodeIdToken(idToken).payload.glopla_id) {
            // MEMO: IDトークンからlocaleを取得し、言語切替を行う
            i18n.changeLanguage(decodeIdToken(idToken).payload.locale);
            setGloplaId(decodeIdToken(idToken).payload.glopla_id);
          } else {
            const queryParams = {
              error_message_key: 'no_organization_affiliation',
              prompt: 'login',
            };
            reLogin(queryParams);
            return;
          }
          setReturnTo(retrieveReturnTo(storedNonce));
          dispatch(fromPathStore.actions.clearFromPath());
        }

        if (!idToken) {
          handleErrors(new Error('idToken is not found in tokenResponse'));
          return endInFailure();
        }
        try {
          await verifyIdToken(idToken as string, storedNonce);
          dispatch(
            authStore.actions.setSession(tokenProcessing(tokenResponse)),
          );
        } catch (e) {
          handleErrors(e);
          return endInFailure();
        }
        if (areOrganizationsSelectTransferred.enabled) {
          setLoading(false);
          window.location.assign(pages.organizations + '?from=login');
        }
      })();
    } else {
      // 認証エラー画面表示時に「トップへ」ボタンを押下すれば再認証が可能なため、Sentryのエラー通知は行わない.
      // ※ ただし、後続のトークンリクエストまたはidTokenの取得に失敗した場合においては、Sentryにエラー通知を行う.
      return endInFailure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const areOrganizationsSelectTransferred =
        await enabledFeatureFlagsApi.getDetail('organization_select_transfer');

      if (
        storedToken &&
        gloplaId &&
        !areOrganizationsSelectTransferred.enabled
      ) {
        try {
          const myOrganizations = await affiliationsApi.myOrganizations();
          const organization = myOrganizations.find(
            (org) => org.organization.gloplaId === gloplaId,
          );

          if (!organization) {
            const queryParams = {
              error_message_key: 'no_organization_affiliation',
              prompt: 'login',
            };
            reLogin(queryParams);
            return;
          }
          dispatch(authStore.actions.selectOrganization(organization));
          setLoading(false);

          if (returnTo) {
            window.location.assign(returnTo);
          } else {
            window.location.assign(pages.top);
          }
          return;
        } catch (e) {
          handleErrors(e as Error);
          endInFailure();
          return;
        }
      }
    })();
  }, [storedToken, gloplaId, dispatch, returnTo, handleErrors]);

  return (
    <>
      {loading && <CenteredLoader />}
      {openIdError && <OpenIdError />}
    </>
  );
};
